import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle } from "lucide-react";
import silencePopSound from "../../Assets/sounds/silencepop.mp3";

const SilenceDetector = ({ 
  isRecording, 
  wsRef,
  onStopRecording,
  silenceThreshold = 0.1, // 10% volume threshold
  silenceTimeout = 5000, // 5 second timeout before warning
  countdownDuration = 3, // 3 second countdown (Total 8s)
  externalAnalyser = null // Optional externally provided analyser
}) => {
  // State variables
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [silenceDetected, setSilenceDetected] = useState(false);
  const [countdownTime, setCountdownTime] = useState(countdownDuration);
  
  // Refs
  const silenceTimer = useRef(null);
  const countdownTimer = useRef(null);
  const lastAudioLevel = useRef(0);
  const animationFrame = useRef(null);
  const isActive = useRef(false); // Track if monitoring is active
  const silenceAudioRef = useRef(null);
  const autoStopTriggered = useRef(false); // Track if auto-stop has been triggered
  // Mirrors `silenceDetected` state but is always current inside the
  // requestAnimationFrame loop (which closes over stale state values).
  const silenceDetectedRef = useRef(false);

  // Initialize the audio element
  useEffect(() => {
    silenceAudioRef.current = new Audio(silencePopSound);
  }, []);

  // Play sound when silence is detected
  useEffect(() => {
    if (silenceDetected && isRecording) {
      silenceAudioRef.current.play().catch(err => {
        console.error("Error playing silence detection sound:", err);
      });
    }
  }, [silenceDetected, isRecording]);
  
  // Set up audio monitoring when recording starts or analyser becomes available
  useEffect(() => {
    if (isRecording && !isActive.current) {
      isActive.current = true;
      autoStopTriggered.current = false; // Reset flag when recording starts
      setupAudioMonitoring();
    } else if (!isRecording && isActive.current) {
      cleanup();
      isActive.current = false;
    }
    
    // If we are recording and an external analyser becomes available, use it
    if (isRecording && isActive.current && externalAnalyser && !analyser) {
        setupAudioMonitoring();
    }

    return () => {
      if (isActive.current) {
        cleanup();
        isActive.current = false;
      }
    };
  }, [isRecording, externalAnalyser]);
  
  const setupAudioMonitoring = async () => {
    if (externalAnalyser) {
      console.log("🎤 SilenceDetector: Using externally provided analyser.");
      setAnalyser(externalAnalyser);
      monitorAudioLevels(externalAnalyser);
      return;
    }

    try {
      console.log("🎤 SilenceDetector: Requesting microphone access...");
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      
      // Create audio context and analyser
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const audioAnalyser = context.createAnalyser();
      audioAnalyser.fftSize = 256;
      
      // Connect microphone to analyser
      const microphone = context.createMediaStreamSource(stream);
      microphone.connect(audioAnalyser);
      
      setAudioContext(context);
      setAnalyser(audioAnalyser);
      
      // Start monitoring audio levels
      monitorAudioLevels(audioAnalyser);
    } catch (error) {
      console.error("🎤 SilenceDetector: Error setting up audio monitoring:", error);
      isActive.current = false;
    }
  };
  
  // Monitor audio levels and detect silence
  const monitorAudioLevels = (audioAnalyser) => {
    if (!audioAnalyser || !isActive.current) return;
    
    // Ensure context is resumed if using internal context
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    const bufferLength = audioAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const checkAudioLevel = () => {
      if (!isActive.current || !isRecording || autoStopTriggered.current) {
        // Stop monitoring if no longer recording or auto-stop has been triggered
        if (animationFrame.current) {
          cancelAnimationFrame(animationFrame.current);
          animationFrame.current = null;
        }
        return;
      }
      
      audioAnalyser.getByteFrequencyData(dataArray);
      
      // Calculate average volume level
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength / 255; // Normalize to 0-1
      lastAudioLevel.current = average;

      // Debug logging every 100 frames (roughly 1.6s)
      if (Math.random() < 0.01) {
        console.log(`🎤 SilenceDetector: Audio level: ${average.toFixed(4)}, Threshold: ${silenceThreshold}`);
      }
      
      // Check for silence
      if (average < silenceThreshold) {
        if (!silenceTimer.current && !silenceDetectedRef.current && !autoStopTriggered.current) {
          silenceTimer.current = setTimeout(() => {
            if (lastAudioLevel.current < silenceThreshold && isActive.current && isRecording && !autoStopTriggered.current) {
              setSilenceDetected(true);
              silenceDetectedRef.current = true;
              startCountdown();
            }
          }, silenceTimeout);
        }
      } else {
        // Reset silence timer if audio detected
        if (silenceTimer.current) {
          clearTimeout(silenceTimer.current);
          silenceTimer.current = null;
        }

        // If the "silence detected" dialog / countdown is already showing
        // and the user has started speaking again at normal volume,
        // dismiss it automatically instead of waiting for the countdown
        // to finish or for a manual click.
        if (silenceDetectedRef.current && !autoStopTriggered.current) {
          handleContinue();
        }
      }
      
      // Continue monitoring
      animationFrame.current = requestAnimationFrame(checkAudioLevel);
    };
    
    // Start the monitoring loop
    animationFrame.current = requestAnimationFrame(checkAudioLevel);
  };
  
  // Start countdown for automatic stop
  const startCountdown = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
    }
    
    setCountdownTime(countdownDuration);
    
    countdownTimer.current = setInterval(() => {
      setCountdownTime((prev) => {
        if (prev <= 1) {
          if (countdownTimer.current) {
            clearInterval(countdownTimer.current);
            countdownTimer.current = null;
          }
          handleAutoStop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // Continue recording and reset silence detection
  const handleContinue = () => {
    if (autoStopTriggered.current) return; // Prevent actions if already stopping
    
    setSilenceDetected(false);
    silenceDetectedRef.current = false;
    
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
  };
  
  // Automatically stop recording after countdown
  const handleAutoStop = () => {
    // Prevent multiple calls
    if (autoStopTriggered.current) return;
    
    autoStopTriggered.current = true;
    
    // Clear timers first to prevent any further calls
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
    
    // Check if WebSocket is open and we're still recording
    if (wsRef.current?.readyState === WebSocket.OPEN && isRecording) {
      console.log('Calling onStopRecording due to silence timeout');
      onStopRecording();
    }
    
    setSilenceDetected(false);
    silenceDetectedRef.current = false;
    
    // Ensure all resources are cleaned up
    cleanup();
  };
  
  // Clean up resources
  const cleanup = () => {
    // Cancel animation frame first
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    
    // Clear timers
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
    
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    
    // Stop media stream tracks
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    
    // Close audio context
    if (audioContext) {
      audioContext.close().catch(err => console.error("Error closing audio context:", err));
      setAudioContext(null);
      setAnalyser(null);
    }
    
    // Reset states
    setSilenceDetected(false);
    silenceDetectedRef.current = false;
    lastAudioLevel.current = 0;
  };
  
  return (
    <AnimatePresence>
      {silenceDetected && isRecording && !autoStopTriggered.current && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed inset-x-0 top-1/4 mx-auto w-11/12 max-w-md bg-white rounded-lg shadow-lg z-50 overflow-hidden border border-orange-200"
        >
          <div className="bg-orange-50 p-4 border-b border-orange-100">
            <div className="flex items-center">
              <AlertCircle className="text-orange-500 mr-2" size={24} />
              <h3 className="text-lg font-medium text-gray-800">Silence Detected</h3>
            </div>
          </div>
          
          <div className="p-4">
            <p className="text-gray-600 mb-4">
              No voice has been detected for 5 seconds. To save your session credits, the recording will stop automatically in:
            </p>
            
            <div className="mb-4 flex justify-center">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-xl font-bold text-orange-600">{countdownTime}</span>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleAutoStop}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors"
              >
                Stop Recording
              </button>
              <button
                onClick={handleContinue}
                className="px-4 py-2 text-white bg-orange-500 hover:bg-orange-600 rounded-md text-sm font-medium transition-colors"
              >
                Continue Recording
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SilenceDetector;